/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styles from './idle-fee-block.styles';
import clockIcon from '../../../../assets/icons/clock.svg';
import { ITransaction } from '../../../../api/transactions/types';

type Props = {
    guest: ITransaction;
    showTimer?: boolean;
};

const MS_IN_MINUTE = 60_000;

const formatMmSs = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const mm = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, '0');
    const ss = (totalSeconds % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
};

const IdleFeeBlock = (props: Props) => {
    const { guest, showTimer = true } = props;
    const intl = useIntl();
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);

    const calculatedAtMs = useMemo(() => {
        const ts = guest.idleFeeCalc?.timestamp;
        const parsed = ts ? Date.parse(String(ts)) : NaN;
        return Number.isNaN(parsed) ? null : parsed;
    }, [guest.idleFeeCalc?.timestamp]);

    const baseRef = useRef<{ elapsedMs: number; atMs: number; calculatedAtMs: number | null }>({
        elapsedMs: 0,
        atMs: now,
        calculatedAtMs: null,
    });

    const calc = guest.idleFeeCalc;
    if (baseRef.current.calculatedAtMs !== calculatedAtMs) {
        const countedMs = (calc?.minutesTotal ?? 0) * MS_IN_MINUTE;
        const serverNow = calc?.now ? Date.parse(String(calc.now)) : NaN;
        const sinceCalcMs =
            calculatedAtMs && !Number.isNaN(serverNow) ? Math.max(0, serverNow - calculatedAtMs) : 0;

        baseRef.current = {
            elapsedMs: countedMs + sinceCalcMs,
            atMs: Date.now(),
            calculatedAtMs,
        };
    }

    const elapsedMs = baseRef.current.elapsedMs + Math.max(0, now - baseRef.current.atMs);
    const graceMs = (guest.idleFeeCalc?.minutesGrace ?? 0) * MS_IN_MINUTE;
    const graceLeftMs = Math.max(0, graceMs - elapsedMs);
    const isGraceRunning = graceLeftMs > 0;

    const charged = useMemo(() => {
        const cents = guest.idleFeeCalc?.price ?? guest.idlePrice ?? 0;
        return Number(cents / 100).toFixed(2);
    }, [guest.idleFeeCalc?.price, guest.idlePrice]);

    const tariffGrace = guest.pricing?.idleFee?.graceMinutes ?? 0;
    const title = !showTimer
        ? tariffGrace
          ? intl.formatMessage({ id: 'idleFee.pending' }, { grace: tariffGrace })
          : intl.formatMessage({ id: 'idleFee.pendingNoGrace' })
        : !calculatedAtMs
          ? intl.formatMessage(
                { id: 'idleFee.calculating' },
                { charged, currency: guest.pricing.currency }
            )
          : isGraceRunning
          ? intl.formatMessage({ id: 'idleFee.startsIn' }, { time: formatMmSs(graceLeftMs) })
          : intl.formatMessage(
                { id: 'idleFee.running' },
                { time: formatMmSs(elapsedMs - graceMs), charged, currency: guest.pricing.currency }
            );

    const hint = intl.formatMessage({ id: 'idleFee.unplugToEnd' });

    return (
        <div css={styles.block}>
            <img css={styles.icon} src={clockIcon} alt="clock" />
            <div css={styles.content}>
                <div css={styles.titleRow}>
                    <div css={styles.title}>{title}</div>
                </div>
                <div css={styles.subtitle}>{hint}</div>
            </div>
        </div>
    );
};

export default IdleFeeBlock;
